/**
 * The persistent operator rail (DESIGN.md §4.1) — on every screen except
 * Login. nav landmark 'Operator tools' (net-locked). 208px, navy-900;
 * active item = gold indicator + gold-400 label. Rail rows are 56px MINIMUM,
 * and a row whose label wraps to two lines is taller — see the note on
 * `railScreen` below, and §4.3 for the measured per-row figures.
 *
 * ⭐ T1749 M4 — THIS FILE IS THE ROW LIST AND NOTHING ELSE. The markup, the
 * classes and the landmark live in `@ecmb/ui`'s `Rail`, which both tools' CSS
 * now shares; what stays here is the part that is the KIOSK's: which
 * destinations exist, which store selector feeds each badge, how they group, and
 * which detail screens collapse onto which row.
 *
 * ⭐ T1749 M6 — THE ROW LIST IS R6'S (the sitting, 2026-09-10), AND THE HEADROOM
 * ARGUMENT THE OLD COMMENTS HERE CARRIED IS RETIRED WITH THEM. Three changes,
 * one commit: `Quote tool` joins ABOVE `Web enquiries`; `Flagged quotes` leaves
 * (C2); `Price changes` moves into the labelled `Admin` group. The row COUNT is
 * unchanged at eight — one in, one out — which is why the group geometry below
 * did not have to move.
 *
 * ⛔ EVERY FIGURE THIS FILE USED TO STATE (699px intrinsic, "8 unbadged rows are
 * 677px", "one 56px row takes it to 755px") IS GONE RATHER THAN UPDATED. They
 * were measured on the PRE-R6 rail and R6 itself ruled they must be re-derived
 * before they are cited again. The re-derived figures, their badge state and
 * their command live in DESIGN.md §4.3, in one place; the gate that holds them
 * is SC-10b. Re-derive, never quote from here:
 *   cd e2e && npx playwright test tests/green/tablet-ia.spec.ts \
 *     --project=journeys -g "SC-10"
 *
 * ⛔ ADDING A NINTH ROW STILL NEEDS A FRESH LIVE MEASUREMENT. The budget is the
 * ~700px Safari-tab plane (R11, DESIGN.md §4.3) and the rail sits inside it with
 * single-digit pixels to spare, not with room.
 *
 * Net strict-mode rule (M1 session log): exactly ONE button named
 * 'Web enquiries' exists app-wide — this rail entry, with the live count as
 * REAL TEXT inside the button subtree (bug-f contract).
 */
import { Rail as SharedRail, type RailBadge, type RailGroup } from '@ecmb/ui';
import type { AppState, ScreenId } from '../store/state.js';
import { inboxBadge, rateChangesBadge, portalBadge, type BadgeView } from '../store/selectors.js';
import {
  InboxIcon,
  BookingsIcon,
  BusIcon,
  RatesIcon,
  PriceChangesIcon,
  OperatorsIcon,
  NursingHomeIcon,
  LogOutIcon,
} from './icons.js';

export type RailProps = {
  state: AppState;
  onNavigate: (screen: ScreenId) => void;
  onHome: () => void;
  onLogout: () => void;
};

/**
 * ⚠️ `BadgeView` IS THE SELECTORS' OWN TYPE and `RailBadge` IS THE SHARED
 * COMPONENT'S, so this is the seam between them rather than a formality.
 *
 * ⛔ THE ONE FIELD IT TRANSLATES IS `kind`, AND THAT TRANSLATION IS THE FIX FOR
 * D9's LAST DRIFT (T1749 M6, Captain's M5 check item 4). `Rail`'s badge is
 * composed by `chipClass({ shape: 'count', count })`, whose vocabulary is
 * `ChipCount` — `default | error`. The kiosk's selectors say `count | error`,
 * because to a SELECTOR the opposite of an error is a count. Both spellings are
 * right where they live; what was wrong was the rail hand-writing a third
 * spelling into the class attribute. So the widening happens HERE, once, where
 * both vocabularies are in view, and never as a cast.
 */
function badge(view: BadgeView): RailBadge {
  return view === null ? null : { text: view.text, kind: view.kind === 'error' ? 'error' : 'default' };
}

/**
 * Which rail row a screen lights. ⛔ A screen reached THROUGH another collapses
 * onto the row that leads to it: a booking's detail screen is still 'Bookings'
 * to the rail, or the operator loses the one mark saying where in the tool she
 * is.
 *
 * ⭐ T1749 M6 — `catalog` JOINED THE SAME RULE, and its absence was a real hole
 * rather than a filing one (impeccable round 1, observation 6). The catalog is a
 * top-level ScreenId reached only from the `Price lists` shelf, so with no entry
 * here it lit NOTHING: all eight rows read plain `rail-item` with a transparent
 * indicator, and the rail stopped answering "where am I" on the one screen a
 * reviewer spends a sitting inside. It is the `booking-detail` shape exactly —
 * a destination whose only route in is a list row.
 * ⚠️ THE COLLAPSE IS A PROJECTION, NOT A NAVIGATION: it changes which row is
 * MARKED, never where a tap goes. `state.screen` stays `catalog`.
 */
function railScreen(screen: ScreenId): ScreenId {
  if (screen === 'booking-detail') return 'bookings';
  if (screen === 'catalog') return 'admin';
  return screen;
}

export function Rail({ state, onNavigate, onHome, onLogout }: RailProps) {
  const groups: RailGroup<ScreenId>[] = [
    {
      items: [
        /* ⭐ T1749 M6 (R6, and half of C1) — THE QUOTE TOOL IS A ROW NOW, AND IT
           IS THE VISIBLE HALF OF A PAIR. Alex, at the sitting: "the ECMB logo
           button should be the name only and a new `Quote tool` tab should hold
           today's calculator". Until now the ONLY way to the calculator from
           anywhere else in the tool was tapping the wordmark — a control that
           looked like a logo and behaved like a button, which is the discovery
           problem R6 is fixing. The mark keeps its accessible name and its
           action (the net has driven `Home` since M2, and DS-7 forbids changing
           a net-locked accessible name); what it loses is every affordance that
           said "press me". THIS row is what says it instead.

           ⛔ IT GOES THROUGH `onHome`, NEVER `navigate`, and the difference is
           not cosmetic. `goHome` is idempotent and CLEARS the nav stack;
           `navigate('home')` would PUSH onto it, so Back would walk the
           operator through every time she reached for the calculator. The
           dispatch is in the `onNavigate` prop below, so the shared component
           keeps knowing nothing about the kiosk's screen graph.
           ⚠️ It still lights from `activeScreen`: `state.screen === 'home'` is
           the home screen, and `railScreen` passes it straight through. */
        {
          screen: 'home',
          label: 'Quote tool',
          icon: <BusIcon />,
          primary: true,
        },
        {
          screen: 'inbox',
          label: 'Web enquiries',
          icon: <InboxIcon />,
          badge: badge(inboxBadge(state)),
          primary: true,
        },
        /* T928 slice 1 — PROMOTED out of the price-book screen's quiet-entry row
           to sit beside Web enquiries. The grouping rule Alex ruled is WHAT a
           thing is, not which system sent it: a nursing home asking for a bus
           and a customer asking through the website are the same KIND of work,
           so they are neighbours — with a live count each, for the same reason.
           The count is requests still WAITING (`status === 'new'`), not the list
           length: a badge that can never reach zero reads as broken.

           ⭐ T939 M5 — THIS ROW WAS `Nursing home requests` AND IS NOW THE
           CONTROL CENTRE (Alex, 2026-08-10, prototype reviewed directly). It
           REPLACED that row rather than joining it.

           ⚠️ The badge still counts WAITING REQUESTS, not homes and not nurses.
           Alex ruled it carries over so Helen keeps her at-a-glance count; the
           requests section is the first thing on the destination, which is what
           keeps the referent obvious. */
        {
          screen: 'nursing-homes',
          label: 'Nursing homes',
          icon: <NursingHomeIcon />,
          badge: badge(portalBadge(state)),
          primary: true,
        },
        { screen: 'bookings', label: 'Bookings', icon: <BookingsIcon />, primary: true },
      ],
    },
    /* T660 M6 (P0-23 + P0-21). A 'Coming soon' group sat here: three
       DISABLED Phase-2 rows (Calendar / Drivers / Vehicles) under their own
       label. It is gone, and the two reasons are one reason.
         - It advertised absent features to the only two people who already
           know they are absent, on the tool they use all day.
         - It cost about 222px of a 768px rail (3 x 56px rows + the group
           label + the group's top margin), which is what pushed 'Log out'
           below the fold on a 1024x768 tablet (SC-10). Nothing else in the
           rail was removable, so this group WAS the overflow.
       The regression pin is an ABSENCE assertion in
       test/client/shell.spec.tsx plus the SC-10 viewport probe in
       e2e/tests/green/tablet-ia.spec.ts - the latter in Playwright and not
       jsdom on purpose, because jsdom has no layout engine and would assert
       0 <= 768 forever. */
    /* ⭐ T928 slice 1 — THE PRICE GROUP CARRIES NO LABEL, deliberately.
       Alex ruled the rail item is `Price lists`, and named no caption for
       the group holding it. `.rail-group` is `margin-top` only — no border,
       no ::before — so the label is genuinely optional and an unlabelled group
       renders as pure separation. That is the whole reason this shape was
       chosen: it groups by WHAT these are without inventing customer-visible
       copy nobody approved, and a caption is one line to add later if Alex
       wants one.
       ⛔ `Prices` was considered and rejected: the account book inside this
       very screen has a `Prices` TAB, and two surfaces with one name is
       against the test this whole slice is graded by ("could Helen find each
       surface without being told where it moved").
       ⭐ T1749 M6 (R6) — THE GROUP IS ONE ROW NOW. `Price changes` moved to
       Admin below and `Flagged quotes` left the rail entirely (C2), so what is
       left is the price BOOK on its own. The unlabelled separation stays: a
       group of one still says "this is a different kind of thing", and
       captioning it now would be inventing the copy the paragraph above
       declined to invent. */
    {
      items: [{ screen: 'admin', label: 'Price lists', icon: <RatesIcon /> }],
    },
    /* ⭐ T1749 M6 (R6) — ADMIN HOLDS TWO ITEMS, AND `Price changes` IS THE ONE
       THAT MOVED. Alex at the sitting: "move Price changes under Admin"
       (recorded as R6, "a decision, not something I am looking to debate").
       ⚠️ It reads against T928's grouping rule — the screen is where a drift
       between `docs/pricing.json` and the database surfaces, which is price
       work — and the ruling wins over the rule, which is what a ruling is for.
       The reading that makes it consistent rather than merely obeyed: it is the
       reconcile surface, opened when something is WRONG, not a price list Helen
       works from; Admin is the rare-configuration corner, and that is where it
       now sits.
       ⛔ `Flagged quotes` IS NOT HERE EITHER — C2: the ROW is gone, the `triage`
       ScreenId, its `App.tsx` switch arm and `screens/Triage.tsx` all stay, so
       re-enabling it is one line. `shell.spec.tsx` asserts the row's absence and
       `admin-screens.spec.tsx` still renders the screen directly. The queue's
       lost reader is Q134 in `docs/OPEN-QUESTIONS.md`. */
    {
      label: 'Admin',
      items: [
        {
          screen: 'rate-changes',
          label: 'Price changes',
          icon: <PriceChangesIcon />,
          badge: badge(rateChangesBadge(state)),
        },
        { screen: 'operators', label: 'Who can sign in', icon: <OperatorsIcon /> },
      ],
    },
  ];

  return (
    <SharedRail<ScreenId>
      label="Operator tools"
      groups={groups}
      activeScreen={railScreen(state.screen)}
      /* ⛔ ONE ROW DOES NOT NAVIGATE, AND THE DISPATCH IS HERE RATHER THAN IN A
         PROP ON THE ROW. `Quote tool` is `goHome` (see its own note above), and
         teaching `@ecmb/ui`'s `Rail` about a per-row handler would give the
         shared component a second way to act — which is the API surface
         `RAIL_PROP_KEYS` exists to keep closed. The kiosk owns its screen graph;
         this is one line of it. */
      onNavigate={(screen) => {
        if (screen === 'home') onHome();
        else onNavigate(screen);
      }}
      onBrand={onHome}
      /* T642 M6: the value is the SERVER's `display_name`, fetched from
         GET /api/me at sign-in (lib/actions.ts doLogin) rather than seeded
         from whatever was typed into the login form. It is the same string
         the server writes into every `actor` column, so what Helen reads here
         and what the audit trail records are one name by construction. */
      operator={state.operator}
      logOutLabel="Log out"
      logOutIcon={<LogOutIcon />}
      onLogOut={onLogout}
    />
  );
}
